import {
    EditableLabel,
    getCornerRadiusFromSize,
} from 'pages/easy-estimator/components/scrollable-area/design-countertop/drawer/editableLabel';

import { formatCornerRadiusLabel } from '@canvas-common';
import { DEFAULT_CORNER_RADIUS } from '@common/constants';
import {
    ApplianceModel,
    CornerInfo,
    CornerRadiusModel,
    DrawingAppliance,
    DrawingShape,
    EdgeModel,
    PlumbingProduct,
} from '@common/models';
import { OptionalItem } from '@common/models/optional-items-response';
import { selectSortedEdges } from '@ee-api/edges-api/useEdges';

import { CornerInfoWithProductInfo, SelectedItemWithFullRecord } from '../interfaces/ProductMatchingSummary';
import { createEquivalentFinder, createIsInCatalogFinder } from '../utils/useEquivalentProductMatching.utils';

/**
 * I product item like EdgeModel
 * R Raw catalog item
 * N Design item like DrawingShape
 */
export type CheckerContext<R> = {
    newCatalog: R[];
};

export type BuilderCommonArgs<I, R, N> = {
    oldValue: N[];
    item: I;
    index: number;
    ctx: CheckerContext<R>;
};

export type EquivalentProductChecker<I, R, N> = {
    existsInNewCatalog: (args: { item: I; ctx: CheckerContext<R> }) => boolean;
    findEquivalent: (args: { item: I; ctx: CheckerContext<R> }) => I | undefined;
    /**
     * Create a new design item N[] keeping the product Item I
     */
    buildNewDesignItemOnKeepingItem: (args: BuilderCommonArgs<I, R, N>) => N[];
    /**
     * Create a new design item N[] replacing the product Item I with an equivalent
     */
    buildNewDesignItemOnReplacingWithEquivalent: (
        args: BuilderCommonArgs<I, R, N> & {
            equivalent: I;
        }
    ) => N[];
    /** How to define a default. The default is used when the product does not exist and no equivalent is found. If is not defined. The item would be removed instead. You also need to define how to build the new value using the default in {@link buildNewValueUsingDefault} */
    selectDefault?: (args: { ctx: CheckerContext<R> }) => I;
    /** How to  build a design item from the default. It  is used when the product does not exist and no equivalent is found. If is not defined. The item would be removed instead. You also need to define how to select the default using the default in {@link selectDefault} */
    buildNewValueUsingDefault?: (args: BuilderCommonArgs<I, R, N> & { defaultItem: I }) => N[];
    buildNewDesignItemOnRemoving?: (args: BuilderCommonArgs<I, R, N>) => N[];
};

export const SelectedItemChecker: EquivalentProductChecker<
    SelectedItemWithFullRecord,
    OptionalItem | PlumbingProduct,
    SelectedItemWithFullRecord
> = {
    existsInNewCatalog: ({ item: { selectedItem }, ctx: { newCatalog } }) =>
        newCatalog.some((catalogItem) => catalogItem.productId === selectedItem.productId),
    findEquivalent: ({ item, ctx: { newCatalog } }) => {
        const rawEquivalent = newCatalog.find(createEquivalentFinder(item.selectedItem.masterSku));
        return rawEquivalent
            ? { ...item, selectedItem: { ...item.selectedItem, productId: rawEquivalent?.productId } }
            : undefined;
    },
    buildNewDesignItemOnKeepingItem: ({ oldValue, item }) => {
        return [...oldValue, item];
    },
    buildNewDesignItemOnReplacingWithEquivalent: ({ oldValue, equivalent }) => {
        return [...oldValue, equivalent];
    },
};

const isADiagonalCorner = (corner: CornerInfo) => !!corner?.diagonalLength;
const isARadiusCorner = (corner: CornerInfo) => !isADiagonalCorner(corner);

export const CornerChecker: EquivalentProductChecker<CornerInfoWithProductInfo, CornerRadiusModel, DrawingShape> = {
    existsInNewCatalog: ({ item, ctx: { newCatalog } }) =>
        newCatalog.some((catalogItem) => catalogItem.productId === item.cornerInfo.productId),
    findEquivalent: ({ item, ctx: { newCatalog } }) => {
        const diagonalEquivalent = isADiagonalCorner(item.cornerInfo)
            ? newCatalog.find(createEquivalentFinder(item.masterSku))
            : undefined;
        // for radius corners, the equivalent is based on range, not sku
        const radiusEquivalent =
            isARadiusCorner(item.cornerInfo) && item.cornerInfo.size
                ? getCornerRadiusFromSize(item.cornerInfo.size, newCatalog)
                : undefined;
        const equivalentProduct = diagonalEquivalent ?? radiusEquivalent;
        if (equivalentProduct) {
            const newCornerInfo = CornerInfo.clone(item.cornerInfo);
            newCornerInfo.productId = equivalentProduct?.productId;
            return { ...item, cornerInfo: newCornerInfo };
        }
        return undefined;
    },
    buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,
    buildNewDesignItemOnReplacingWithEquivalent: ({ oldValue: clonedDrawingShapes, index, equivalent, item }) => {
        Object.entries(clonedDrawingShapes[index].cornerMap).forEach(([_, cornerInfo]) => {
            if (cornerInfo.productId && cornerInfo.productId === item.cornerInfo.productId) {
                cornerInfo.productId = equivalent.cornerInfo.productId;
            }
        });

        const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(index, clonedDrawingShapes[index]);
        return newValueAfterReplacements;
    },
    buildNewDesignItemOnRemoving: ({ oldValue: clonedDrawingShapes, index, item }) => {
        Object.entries(clonedDrawingShapes[index].cornerMap).forEach(([_, cornerInfo]) => {
            if (cornerInfo.productId && cornerInfo.productId === item.cornerInfo.productId) {
                cornerInfo.productId = undefined;
                cornerInfo.diagonalLength = undefined;
                cornerInfo.size = DEFAULT_CORNER_RADIUS;
                cornerInfo.label = formatCornerRadiusLabel(cornerInfo.size);
                if (!cornerInfo.labelMeasure?.text) {
                    cornerInfo.labelMeasure = EditableLabel.newMeasureText(
                        cornerInfo.label,
                        cornerInfo.labelMeasure?.fill
                    );
                }
                if (!cornerInfo.labelText?.text) {
                    cornerInfo.labelText = EditableLabel.newLabelText(cornerInfo.label);
                }
                cornerInfo.labelMeasure.set('text', cornerInfo.label);
                cornerInfo.labelText.set('text', cornerInfo.label);
            }
        });
        const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(index, clonedDrawingShapes[index]);
        return newValueAfterReplacements;
    },
};

export const ApplianceChecker: EquivalentProductChecker<DrawingAppliance, ApplianceModel, DrawingShape> = {
    existsInNewCatalog: ({ item, ctx }) => ctx.newCatalog.some(createIsInCatalogFinder(item.applianceModel.productId)),
    findEquivalent: ({ item, ctx }) => {
        const equivalentApplianceModel = ctx.newCatalog.find(createEquivalentFinder(item.applianceModel.masterSku));
        return equivalentApplianceModel ? { ...item, applianceModel: equivalentApplianceModel } : undefined;
    },
    buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,
    buildNewDesignItemOnReplacingWithEquivalent: ({ item, oldValue: clonedDrawingShapes, index, equivalent }) => {
        //Mutating drawing shape appliances in clone
        clonedDrawingShapes[index].appliances.forEach((drawingAppliance) => {
            if (drawingAppliance.applianceModel.productId === item.applianceModel.productId) {
                drawingAppliance.applianceModel = equivalent.applianceModel;
            }
        });

        const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(index, clonedDrawingShapes[index]);
        return newValueAfterReplacements;
    },
    buildNewDesignItemOnRemoving: ({ item, oldValue: clonedDrawingShapes, index }) => {
        //Mutating drawing shape appliances in clone
        clonedDrawingShapes[index].appliances = clonedDrawingShapes[index].appliances.filter(
            (drawingAppliance) => drawingAppliance.applianceModel.productId !== item.applianceModel.productId
        );
        const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(index, clonedDrawingShapes[index]);
        return newValueAfterReplacements;
    },
};

export const EdgeChecker: EquivalentProductChecker<EdgeModel, EdgeModel, DrawingShape> = {
    existsInNewCatalog: ({ item: edge, ctx: { newCatalog } }) =>
        newCatalog.some(createIsInCatalogFinder(edge.productId)),
    findEquivalent: ({ item: edge, ctx: { newCatalog } }) => {
        /** There might be more than one equivalent. We want to find the first given the default sorting for edges */
        const sortedEdges = selectSortedEdges(newCatalog);
        return sortedEdges.find(createEquivalentFinder(edge.masterSku));
    },
    buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,
    buildNewDesignItemOnReplacingWithEquivalent: ({ oldValue: clonedDrawingShapes, index, equivalent }) => {
        clonedDrawingShapes[index].edge = equivalent;

        const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(index, clonedDrawingShapes[index]);
        return newValueAfterReplacements;
    },
    selectDefault: ({ ctx: { newCatalog } }) => {
        const sortedEdges = selectSortedEdges(newCatalog);
        return sortedEdges[0];
    },
    buildNewValueUsingDefault: ({ oldValue: clonedDrawingShapes, index, defaultItem: edgeDefault }) => {
        /**I leave this lines commented on purpose because it seemed a better solution to create a new instance either with lodash's merge or with a the constructor but both won't work. Causes an error related to losing the class methods.*/
        // const _updatedShape = merge(new DrawingShape(clonedDrawingShapes[index]), clonedDrawingShapes[index], { edge: edgeDefault });
        // const updatedShape = new DrawingShape(_updatedShape); // this ensures the new object is a DrawingShape with all methods See drawing-shape-model cloneDrawingShape
        clonedDrawingShapes[index].edge = edgeDefault;
        const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(index, clonedDrawingShapes[index]);
        return newValueAfterReplacements;
    },
};
